import type { DeviceGeofence } from '@/types';

/**
 * geofence — ren geometri för "Nyckel redo"-matchning. Ingen I/O: positionen
 * kommer in som argument, lämnar aldrig modulen och loggas aldrig (GDPR §5.8).
 *
 * Flapping vid fence-gränsen (GPS-jitter) dämpas med:
 *  - hysteres: en matchad cirkel släpps först utanför radie × HYSTERESIS
 *  - dwell: match kräver DWELL_SAMPLES på varandra följande samples innanför
 */

export interface GeoPoint {
  lat: number;
  lon: number;
}

export interface GeofenceMatch {
  deviceId: string;
  /** Avstånd till fence-centrum/-centroid, för sortering (närmast först). */
  distanceMeters: number;
}

export interface GeofenceMatchState {
  /** Matchade enheter (efter dwell), sorterade närmast först. */
  matched: GeofenceMatch[];
  /** På varandra följande samples innanför, per device_id (dwell-räknare). */
  streak: Record<string, number>;
}

export const EMPTY_MATCH_STATE: GeofenceMatchState = { matched: [], streak: {} };

/** Exit-gräns = radie × HYSTERESIS för redan matchade cirklar. */
export const HYSTERESIS = 1.2;
/** Samples i rad innanför som krävs innan en enhet räknas som matchad. */
export const DWELL_SAMPLES = 2;

const EARTH_RADIUS_M = 6371000;

export function haversineMeters(a: GeoPoint, b: GeoPoint): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

export function isInCircle(
  p: GeoPoint,
  center: GeoPoint,
  radiusMeters: number
): boolean {
  return haversineMeters(p, center) <= radiusMeters;
}

/**
 * Ray casting: udda antal korsningar med polygonens kanter = innanför.
 * Punkter exakt på en kant räknas som innanför (>= / <= i on-segment-testet
 * behövs inte för vårt UI-syfte; standardalgoritmen räcker).
 */
export function isPointInPolygon(
  p: GeoPoint,
  polygon: Array<{ lat: number; lng: number }>
): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const yi = polygon[i].lat;
    const xi = polygon[i].lng;
    const yj = polygon[j].lat;
    const xj = polygon[j].lng;
    const intersects =
      yi > p.lat !== yj > p.lat &&
      p.lon < ((xj - xi) * (p.lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function isFiniteInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

/** Defensiv guard: ogiltiga/ofullständiga fences ignoreras i matchningen. */
export function isValidFence(fence: DeviceGeofence): boolean {
  if (!fence.device_id) return false;
  if (fence.geofence_type === 'polygon') {
    return (
      Array.isArray(fence.polygon_coordinates) &&
      fence.polygon_coordinates.length >= 3 &&
      fence.polygon_coordinates.every(
        (c) => isFiniteInRange(c?.lat, -90, 90) && isFiniteInRange(c?.lng, -180, 180)
      )
    );
  }
  return (
    fence.geofence_type === 'circle' &&
    isFiniteInRange(fence.center_latitude, -90, 90) &&
    isFiniteInRange(fence.center_longitude, -180, 180) &&
    isFiniteInRange(fence.radius_meters, 1, 50000)
  );
}

function polygonCentroid(polygon: Array<{ lat: number; lng: number }>): GeoPoint {
  let lat = 0;
  let lon = 0;
  for (const c of polygon) {
    lat += c.lat;
    lon += c.lng;
  }
  return { lat: lat / polygon.length, lon: lon / polygon.length };
}

/**
 * Kör ett positionssample mot fences och föregående tillstånd.
 * En enhet med flera fences matchar om någon av dem träffar.
 */
export function matchGeofences(
  position: GeoPoint,
  fences: DeviceGeofence[],
  prev: GeofenceMatchState
): GeofenceMatchState {
  const byDevice = new Map<string, DeviceGeofence[]>();
  for (const fence of fences) {
    if (!isValidFence(fence)) continue;
    const list = byDevice.get(fence.device_id);
    if (list) list.push(fence);
    else byDevice.set(fence.device_id, [fence]);
  }

  const prevMatched = new Set(prev.matched.map((m) => m.deviceId));
  const streak: Record<string, number> = {};
  const matched: GeofenceMatch[] = [];

  for (const [deviceId, deviceFences] of byDevice) {
    const wasMatched = prevMatched.has(deviceId);
    let inside = false;
    let distance = Number.POSITIVE_INFINITY;

    for (const fence of deviceFences) {
      if (fence.geofence_type === 'polygon') {
        const polygon = fence.polygon_coordinates!;
        inside = inside || isPointInPolygon(position, polygon);
        distance = Math.min(distance, haversineMeters(position, polygonCentroid(polygon)));
      } else {
        const center = { lat: fence.center_latitude!, lon: fence.center_longitude! };
        // Redan matchad cirkel släpps först utanför den vidare exit-gränsen.
        const radius = fence.radius_meters! * (wasMatched ? HYSTERESIS : 1);
        const d = haversineMeters(position, center);
        inside = inside || d <= radius;
        distance = Math.min(distance, d);
      }
    }

    if (!inside) continue;

    // Dwell: en redan matchad enhet förblir matchad; en ny kräver
    // DWELL_SAMPLES samples i rad innanför.
    const count = (prev.streak[deviceId] ?? 0) + 1;
    streak[deviceId] = count;
    if (wasMatched || count >= DWELL_SAMPLES) {
      matched.push({ deviceId, distanceMeters: Math.round(distance) });
    }
  }

  matched.sort((a, b) => a.distanceMeters - b.distanceMeters);
  return { matched, streak };
}

/** true om två tillstånd matchar samma enheter (för att undvika onödig render). */
export function sameMatches(a: GeofenceMatch[], b: GeofenceMatch[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((m, i) => m.deviceId === b[i].deviceId);
}
